import React, { useState } from 'react'
import { IUserObject } from 'types'
import Button from 'components/button'
import EditInfoModal from 'views/profile/components/editInfo/EditInfoModal'
import Contributions from 'views/profile/components/information/components/contributions'
import Public from 'views/profile/components/information/components/public'
import Pinned from 'views/profile/components/information/components/pinned'
import Achievements from 'views/profile/components/information/components/achievements'
import { Organisation, Location, Link, Twitter, LinkedIn } from 'components/icons'
import { validateSourceUrl } from 'helpers/utils'
import Skeleton from 'components/skeleton'

interface InformationProps {
  userInfo?: IUserObject
  ownProfile: boolean
  updateUser: (user: IUserObject) => Promise<void>
}

const Information: React.FC<InformationProps> = ({ userInfo, ownProfile, updateUser }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  const getTwitterHandle = (url: string) => {
    try {
      const handle = url.split('https://twitter.com/')[1].replace('/', '')
      return '@' + handle
    } catch {
      return url
    }
  }

  const getLinkedInHandle = (url: string) => {
    try {
      const handle = url.split('/in/')[1].replace('/', '')
      return handle
    } catch {
      return url
    }
  }

  return (
    <div className="flex justify-center w-full">
      <div className="pt-[30px] w-[90%] mr-auto ml-auto">
        <div className="grid grid-flow-col grid-cols-[auto_0_minmax(0,calc(100%_-_296px_-_24px))] gap-6">
          {/* Profile Information Section */}
          <div className="flex flex-col w-[296px] col-start-1">
            {userInfo ? (
              <img
                className="border-1 border-divider object-cover rounded-full h-[296px] w-[296px]"
                src={validateSourceUrl(userInfo.profilePicture)}
                alt={userInfo.name || 'Annonymous user'}
              />
            ) : (
              <Skeleton className="rounded-full h-[296px] w-[296px]" />
            )}

            <div className="flex flex-col w-full items-center justify-center">
              {userInfo ? (
                <h1 className="text-3xl font-bold mt-5">{userInfo.name}</h1>
              ) : (
                <Skeleton className="mt-5 w-[122px] h-8" />
              )}
              {userInfo ? (
                <div className="mt-2.5 italic">{userInfo.bio}</div>
              ) : (
                <Skeleton className="mt-2.5 w-[200px] h-5" />
              )}
              {ownProfile && (
                <Button className="mt-5" onClick={() => setModalOpen(true)}>
                  Edit Profile
                </Button>
              )}
              <div className="w-full flex flex-col mt-5 text-sm">
                <div className="flex flex-row items-center w-full" title="Organisation">
                  <Organisation />
                  {userInfo ? (
                    <span className="truncate ml-2.5">{userInfo.organisation ? userInfo.organisation : 'Not set'}</span>
                  ) : (
                    <Skeleton className="ml-2.5 w-[50px] h-5" />
                  )}
                </div>
                <div className="flex flex-row items-center w-full mt-[5px]" title="Location">
                  <Location />
                  {userInfo ? (
                    <span className="truncate ml-2.5">{userInfo.location ? userInfo.location : 'Not set'}</span>
                  ) : (
                    <Skeleton className="ml-2.5 w-[50px] h-5" />
                  )}
                </div>
                <div className="flex flex-row items-center w-full mt-[5px]" title="Website">
                  <Link />
                  {userInfo ? (
                    <span className="truncate ml-2.5">
                      {userInfo.website ? (
                        <a href={userInfo.website} target="_blank" rel="noopener noreferrer">
                          {userInfo.website}
                        </a>
                      ) : (
                        'Not set'
                      )}
                    </span>
                  ) : (
                    <Skeleton className="ml-2.5 w-[50px] h-5" />
                  )}
                </div>
                <div className="flex flex-row items-center w-full mt-[5px]" title="Twitter">
                  <Twitter />
                  {userInfo ? (
                    userInfo.twitter ? (
                      <a className="truncate ml-2.5" href={userInfo.twitter} target="_blank" rel="noopener noreferrer">
                        {getTwitterHandle(userInfo.twitter)}
                      </a>
                    ) : (
                      <span className="ml-2.5">Not Set</span>
                    )
                  ) : (
                    <Skeleton className="ml-2.5 w-[50px] h-5" />
                  )}
                </div>
                <div className="flex flex-row items-center w-full mt-[5px]" title="LinkedIn">
                  <LinkedIn />
                  {userInfo ? (
                    userInfo.linkedin ? (
                      <a className="truncate ml-2.5" href={userInfo.linkedin} target="_blank" rel="noopener noreferrer">
                        {getLinkedInHandle(userInfo.linkedin)}
                      </a>
                    ) : (
                      <span className="ml-2.5">Not Set</span>
                    )
                  ) : (
                    <Skeleton className="ml-2.5 w-[50px] h-5" />
                  )}
                </div>
              </div>
            </div>
            <Achievements userId={userInfo?.firebaseUserId} />
          </div>

          {/* Contribution Section */}
          <div className="flex flex-col py-0 px-10 col-[2/span_2]">
            <Pinned userId={userInfo?.firebaseUserId} ownProfile={ownProfile} />
            <Contributions userId={userInfo?.firebaseUserId} />
            <Public userId={userInfo?.firebaseUserId} />
          </div>
        </div>
        {modalOpen && userInfo && ownProfile && (
          <EditInfoModal
            id="edit-information-modal"
            open={modalOpen}
            setOpen={setModalOpen}
            updateUser={updateUser}
            user={userInfo}
          />
        )}
      </div>
    </div>
  )
}

export default Information
